import type { Point2, Polyline2D, Segment2 } from './viewTypes'

// ---------------------------------------------------------------------------
// Encadeamento de segmentos soltos em polilinhas.
//
// A "sopa" de segmentos que sai do corte (ou das arestas de feição
// projectadas) não tem topologia: são pares de pontos independentes,
// com extremos coincidentes a menos de ruído. Aqui soldam-se os
// extremos por proximidade (hash de grelha com vizinhança 3x3, para não
// perder pares que caem em células diferentes) e caminha-se pela
// adjacência.
//
// Trabalha em 2D, DEPOIS da projecção: as linhas de corte vivem todas
// no plano w = 0, por isso a projecção é injectiva e encadear em 2D dá
// exactamente o mesmo que encadear em 3D, com metade das contas.
//
// Best-effort e honesto: o que não fecha sai como polilinha ABERTA.
// Nunca se inventa o segmento de fecho.
// ---------------------------------------------------------------------------

/** Tolerância de solda por omissão, em metros. */
export const LOOP_TOL = 1e-4

export interface AssembleResult {
  /** Polilinhas fechadas (`closed: true`, último ponto não repetido). */
  loops: Polyline2D[]
  /** Polilinhas que não fecharam (`closed: false`). */
  open: Polyline2D[]
}

interface Node {
  x: number
  y: number
  edges: number[]
}

/** Encadeia segmentos em polilinhas. Descarta segmentos degenerados
 *  (extremos coincidentes) e duplicados exactos (mesmo par de nós), que
 *  aparecem sempre que duas faces partilham a aresta cortada ou quando
 *  linhas projectadas se sobrepõem. */
export function assembleLoops(
  segments: Segment2[],
  tol: number = LOOP_TOL,
): AssembleResult {
  const loops: Polyline2D[] = []
  const open: Polyline2D[] = []
  if (!segments || segments.length === 0) return { loops, open }

  const cell = Math.max(tol, 1e-12)
  const nodes: Node[] = []
  const grid = new Map<string, number[]>()

  const keyOf = (ix: number, iy: number): string => `${ix}|${iy}`

  const nodeFor = (p: Point2): number => {
    const ix = Math.round(p[0] / cell)
    const iy = Math.round(p[1] / cell)
    // Vizinhança 3x3: dois pontos a menos de `tol` podem cair em
    // células adjacentes.
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const bucket = grid.get(keyOf(ix + dx, iy + dy))
        if (!bucket) continue
        for (const ni of bucket) {
          const n = nodes[ni]
          if (Math.hypot(n.x - p[0], n.y - p[1]) <= tol) return ni
        }
      }
    }
    const id = nodes.length
    nodes.push({ x: p[0], y: p[1], edges: [] })
    const k = keyOf(ix, iy)
    const bucket = grid.get(k)
    if (bucket) bucket.push(id)
    else grid.set(k, [id])
    return id
  }

  const edgeA: number[] = []
  const edgeB: number[] = []
  const seen = new Set<string>()
  for (const s of segments) {
    if (!s || !s.a || !s.b) continue
    if (
      !Number.isFinite(s.a[0]) ||
      !Number.isFinite(s.a[1]) ||
      !Number.isFinite(s.b[0]) ||
      !Number.isFinite(s.b[1])
    ) {
      continue
    }
    const na = nodeFor(s.a)
    const nb = nodeFor(s.b)
    if (na === nb) continue
    const pairKey = na < nb ? `${na}_${nb}` : `${nb}_${na}`
    if (seen.has(pairKey)) continue
    seen.add(pairKey)
    const ei = edgeA.length
    edgeA.push(na)
    edgeB.push(nb)
    nodes[na].edges.push(ei)
    nodes[nb].edges.push(ei)
  }

  const used = new Array<boolean>(edgeA.length).fill(false)

  const other = (ei: number, n: number): number =>
    edgeA[ei] === n ? edgeB[ei] : edgeA[ei]

  const nextEdge = (n: number): number => {
    for (const ei of nodes[n].edges) {
      if (!used[ei]) return ei
    }
    return -1
  }

  /** Caminha a partir de `startNode` consumindo arestas até esgotar ou
   *  voltar ao início. */
  const walk = (startNode: number): Polyline2D | null => {
    let ei = nextEdge(startNode)
    if (ei < 0) return null
    const points: Point2[] = [[nodes[startNode].x, nodes[startNode].y]]
    let cur = startNode
    let closed = false
    while (ei >= 0) {
      used[ei] = true
      const nxt = other(ei, cur)
      if (nxt === startNode) {
        closed = true
        break
      }
      points.push([nodes[nxt].x, nodes[nxt].y])
      cur = nxt
      ei = nextEdge(cur)
    }
    return { points, closed }
  }

  // Primeiro os extremos livres (grau ímpar): garantem que as
  // polilinhas abertas saem inteiras, e não partidas a meio.
  const starts: number[] = []
  for (let n = 0; n < nodes.length; n++) {
    if (nodes[n].edges.length % 2 === 1) starts.push(n)
  }
  for (const n of starts) {
    let pl = walk(n)
    while (pl) {
      if (pl.points.length >= 2) push(pl, loops, open)
      pl = walk(n)
    }
  }
  // Depois o resto (ciclos e restos de nós de grau par).
  for (let n = 0; n < nodes.length; n++) {
    let pl = walk(n)
    while (pl) {
      if (pl.points.length >= 2 || (pl.closed && pl.points.length >= 3)) {
        push(pl, loops, open)
      }
      pl = walk(n)
    }
  }

  return { loops, open }
}

function push(pl: Polyline2D, loops: Polyline2D[], open: Polyline2D[]): void {
  if (pl.closed && pl.points.length >= 3) loops.push(pl)
  else if (pl.points.length >= 2) open.push({ ...pl, closed: false })
}

/** Todas as polilinhas de um resultado, fechadas primeiro. */
export function allPolylines(r: AssembleResult): Polyline2D[] {
  return [...r.loops, ...r.open]
}
